import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlatformSettings } from '@worki/shared';
import { api } from '../../lib/api.js';
import { Button, Input, Spinner } from '../../components/ui.js';
import { AdminCard, useAdminAction } from './parts.js';

/**
 * ADM-010 — the handful of numbers that belong to Worki rather than to any one
 * business.
 *
 * The VAT rate is the reason this screen exists. It used to sit on every
 * business row: a national figure that changes for everyone at once, stored
 * once per account, where each owner had to find it and get it right. Quotes
 * already written keep the rate they were written under — the change reaches
 * the next quote, never a document a customer has already approved.
 */
export function SettingsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['platform', 'settings'],
    queryFn: () => api.get<PlatformSettings>('/platform/settings'),
  });

  const action = useAdminAction([['platform', 'settings']]);

  const [vatRate, setVatRate] = useState('');
  const [requireApproval, setRequireApproval] = useState(false);
  const [trialDays, setTrialDays] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [alertDays, setAlertDays] = useState('');

  useEffect(() => {
    const data = settings.data;
    if (!data) return;
    setVatRate(String(data.vatRate));
    setRequireApproval(data.requireApproval);
    setTrialDays(String(data.trialDays));
    setSupportEmail(data.supportEmail ?? '');
    setAlertDays(String(data.subscriptionAlertDays));
  }, [settings.data]);

  if (settings.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const save = () =>
    void action
      .run(
        () =>
          api.put('/platform/settings', {
            vatRate: Number(vatRate) || 0,
            requireApproval,
            trialDays: Number(trialDays) || 0,
            supportEmail: supportEmail.trim() === '' ? null : supportEmail.trim(),
            subscriptionAlertDays: Number(alertDays) || 0,
          }),
        'admin.settings.saved',
      )
      .then((ok) => {
        /* The rate reaches the quote editor through the session, which would
           otherwise keep totalling at the old one until the next reload. */
        if (ok) void queryClient.invalidateQueries({ queryKey: ['session'] });
      });

  return (
    <div className="space-y-4">
      <AdminCard title={t('admin.settings.vatRate')} hint={t('admin.settings.vatRateHint')}>
        <div className="flex items-center gap-2">
          <Input
            value={vatRate}
            onChange={(event) => setVatRate(event.target.value.replace(/[^\d.]/g, '').slice(0, 5))}
            inputMode="decimal"
            aria-label={t('admin.settings.vatRate')}
            className="ltr-nums max-w-28"
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
      </AdminCard>

      <AdminCard title={t('admin.settings.requireApproval')} hint={t('admin.settings.requireApprovalHint')}>
        <label className="flex items-center gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={requireApproval}
            onChange={(event) => setRequireApproval(event.target.checked)}
            className="h-4 w-4 shrink-0 rounded accent-primary"
          />
          {t('admin.settings.requireApproval')}
        </label>
      </AdminCard>

      <AdminCard>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{t('admin.settings.trialDays')}</span>
            <Input
              value={trialDays}
              onChange={(event) => setTrialDays(event.target.value.replace(/\D/g, '').slice(0, 3))}
              inputMode="numeric"
              className="ltr-nums"
            />
          </label>

          {/* ADM-011 — the notice period. It governs the trial warning and the
              paid-period warning alike, because what it really sets is how long
              someone here has to do something about either. */}
          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('admin.settings.subscriptionAlertDays')}
            </span>
            <Input
              value={alertDays}
              onChange={(event) => setAlertDays(event.target.value.replace(/\D/g, '').slice(0, 2))}
              inputMode="numeric"
              className="ltr-nums"
            />
            <span className="block text-[11px] text-muted-foreground">
              {t('admin.settings.subscriptionAlertDaysHint')}
            </span>
          </label>

          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{t('admin.settings.supportEmail')}</span>
            <Input
              value={supportEmail}
              onChange={(event) => setSupportEmail(event.target.value)}
              type="email"
              dir="ltr"
              autoComplete="off"
            />
          </label>
        </div>
      </AdminCard>

      <Button onClick={save} loading={action.busy}>
        {t('common.save')}
      </Button>
    </div>
  );
}
